/**
 * MANTENIMIENTO → PLANNER (FASE 8.3, F12.3)
 *
 * «¿Qué trabajos tenemos y quién los hace?», por DÍA o por SEMANA:
 *   · PT (trabajo REAL, materializado o manual): tarjeta sólida; «Mover»
 *     = reprogramar el parte (PATCH), con los avisos de conflicto.
 *   · PROYECCIÓN (ocurrencia de un plan VIGENTE todavía sin parte —la
 *     generación automática cubre los próximos días—): tarjeta discontinua,
 *     rotulada, NO se mueve: para cambiarla se edita el plan.
 *
 * F12.3: vista día y semana, filtro por técnico y por estado, todo en la URL (centro, fecha, vista, técnico,
 * estado) para volver desde un parte al mismo sitio, conflictos explicados (quién, con qué, de qué hora a qué
 * hora, limpieza o mantenimiento) y, en móvil, sólo los días con trabajo.
 *
 * Las OT de Limpieza no se pintan aquí: sólo aparecen como CONFLICTO de una persona AMBAS (misma disponibilidad
 * real). No se fusionan los dos Planner.
 */
import { useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { api, type AlertaMnt, type OpcionesActivos, type ParteResumen, type PlannerMantenimiento } from '../api.ts';
import { EmptyState, Error as ErrorCaja, LeyendaEstados, MarcaDominio, Modal, Panel, useDatos } from '../componentes/base.tsx';
import { EnlaceVolver, EstadoPtEtiqueta, PrioridadEtiqueta, programacion, useVolverAqui, type Volver } from '../componentes/mantenimiento.tsx';
import { ESTADOS_PT, estadoPtMantenimiento } from '../estados.ts';
import { Shell } from '../componentes/shell.tsx';
import { useSesion } from '../contexto.tsx';
import { useListaUrl } from '../listaUrl.ts';
import { Alertas, hoyIso, sumarDiasIso } from './preventivos.tsx';

/** Lunes de la semana de `iso`. */
function lunesDe(iso: string): string {
  const dia = new Date(`${iso}T12:00:00Z`).getUTCDay();
  return sumarDiasIso(iso, dia === 0 ? -6 : 1 - dia);
}

//  FASE 9.5-C: un REABIERTO es trabajo pendiente de atención: se puede volver a programar.
const MOVIBLES = ['ABIERTO', 'PLANIFICADO', 'PENDIENTE', 'REABIERTO'];

/** Filtro por estado: grupos que se entienden, no la lista técnica de estados. */
const GRUPOS_ESTADO: Readonly<Record<string, { texto: string; estados: readonly string[] }>> = {
  hacer: { texto: 'Por hacer', estados: ['ABIERTO', 'PLANIFICADO', 'PENDIENTE', 'REABIERTO'] },
  curso: { texto: 'En curso', estados: ['EN_CURSO'] },
  hechos: { texto: 'Terminados', estados: ['FINALIZADO', 'CERRADO'] },
};

const DEFAULTS = { vista: '', fecha: '', idCentro: '', idOperario: '', estado: '' };

function horario(p: ParteResumen): string {
  if (p.horaProgramada === null) return 'Sin hora';
  if (p.duracionEstimadaMin === null) return p.horaProgramada;
  const [h, m] = p.horaProgramada.split(':').map(Number) as [number, number];
  const fin = h * 60 + m + p.duracionEstimadaMin;
  const hh = Math.floor(fin / 60) % 24;
  return `${p.horaProgramada}–${String(hh).padStart(2, '0')}:${String(fin % 60).padStart(2, '0')}`;
}

export function PaginaPlannerMantenimiento(): ReactNode {
  const { puede } = useSesion();
  const lista = useListaUrl(DEFAULTS);
  const vista = lista.valor('vista') === 'dia' ? 'dia' : 'semana';
  const fecha = lista.valor('fecha') || hoyIso();
  const idCentro = lista.valor('idCentro');
  const idOperario = lista.valor('idOperario');
  const grupo = GRUPOS_ESTADO[lista.valor('estado')] === undefined ? '' : lista.valor('estado');
  const [moviendo, setMoviendo] = useState<ParteResumen | null>(null);
  const [avisos, setAvisos] = useState<AlertaMnt[]>([]);
  const volver = useVolverAqui(vista === 'dia' ? 'el Planner (día)' : 'el Planner (semana)');

  const lunes = lunesDe(fecha);
  const desde = vista === 'dia' ? fecha : lunes;
  const hasta = vista === 'dia' ? fecha : sumarDiasIso(lunes, 6);
  const paso = vista === 'dia' ? 1 : 7;
  const opciones = useDatos(() => api.get<OpcionesActivos>('/api/activos/opciones'), []);
  const tecnicos = useDatos(
    () => api.get<{ filas: { idOperario: string; nombre: string }[] }>(`/api/mantenimiento/planner/tecnicos${idCentro === '' ? '' : `?idCentro=${idCentro}`}`),
    [idCentro],
  );
  const planner = useDatos(
    () => api.get<PlannerMantenimiento>(`/api/mantenimiento/planner?desde=${desde}&hasta=${hasta}`
      + `${idCentro === '' ? '' : `&idCentro=${idCentro}`}${idOperario === '' ? '' : `&idOperario=${idOperario}`}`),
    [desde, hasta, idCentro, idOperario],
  );
  const ir = (f: string): void => lista.fijarVarios({ fecha: f === hoyIso() ? '' : f });
  const dias = vista === 'dia' ? [fecha] : Array.from({ length: 7 }, (_, k) => sumarDiasIso(lunes, k));

  return (
    <Shell migas={[{ texto: 'Mantenimiento' }, { texto: 'Planner' }]} titulo="Planner de mantenimiento">
      <EnlaceVolver />
      <div className="panel">
        {/* FASE 9.1-B: barra en dos niveles — navegación + rango + filtros alineados; leyenda aparte. */}
        <div className="panel-cuerpo planner-mnt-barra" role="toolbar" aria-label="Periodo y filtros">
          <div className="planner-mnt-navegacion" role="group" aria-label="Periodo">
            <div className="planner-mnt-vista" role="group" aria-label="Vista">
              {(['dia', 'semana'] as const).map((v) => (
                <button key={v} type="button" className={vista === v ? 'principal' : 'secundario'} aria-pressed={vista === v}
                  onClick={() => lista.fijar('vista', v === 'dia' ? 'dia' : '')}>{v === 'dia' ? 'Día' : 'Semana'}</button>
              ))}
            </div>
            <button type="button" onClick={() => ir(sumarDiasIso(fecha, -paso))} aria-label={vista === 'dia' ? 'Día anterior' : 'Semana anterior'}>←</button>
            <button type="button" onClick={() => ir(hoyIso())}>{vista === 'dia' ? 'Hoy' : 'Esta semana'}</button>
            <button type="button" onClick={() => ir(sumarDiasIso(fecha, paso))} aria-label={vista === 'dia' ? 'Día siguiente' : 'Semana siguiente'}>→</button>
            <strong className="planner-mnt-rango" aria-live="polite">
              {vista === 'dia' ? programacion(fecha, null) : `${programacion(desde, null)} – ${programacion(hasta, null)}`}
            </strong>
          </div>
          <label className="planner-mnt-filtro">
            <span>Centro</span>
            <select value={idCentro} onChange={(ev) => lista.fijarVarios({ idCentro: ev.target.value, idOperario: '' })}>
              <option value="">Todos</option>
              {(opciones.datos?.centros ?? []).map((c) => <option key={c.id} value={c.id}>{c.cliente.nombre} — {c.nombre}</option>)}
            </select>
          </label>
          <label className="planner-mnt-filtro">
            <span>Técnico</span>
            <select value={idOperario} onChange={(ev) => lista.fijar('idOperario', ev.target.value)}>
              <option value="">Todos</option>
              {(tecnicos.datos?.filas ?? []).map((t) => <option key={t.idOperario} value={t.idOperario}>{t.nombre}</option>)}
            </select>
          </label>
          <label className="planner-mnt-filtro">
            <span>Estado</span>
            <select value={grupo} onChange={(ev) => lista.fijar('estado', ev.target.value)}>
              <option value="">Todos</option>
              {Object.entries(GRUPOS_ESTADO).map(([k, g]) => <option key={k} value={k}>{g.texto}</option>)}
            </select>
          </label>
        </div>
        <div className="panel-cuerpo planner-mnt-leyenda" role="group" aria-label="Leyenda">
          {/* UX-1C: leyenda de ESTADOS (badge + acento) y, aparte, de tipo de tarjeta y de conflicto. */}
          <LeyendaEstados titulo="Estados de PT" estados={Object.values(ESTADOS_PT)}
            extra={<>
              <li className="leyenda-extra"><span className="muestra-pt">Parte (real)</span></li>
              <li className="leyenda-extra"><span className="muestra-proyeccion">Previsto (aún sin parte)</span></li>
              <li className="leyenda-extra"><span aria-hidden="true">⚠</span> Conflicto: se explica en la tarjeta</li>
            </>} />
        </div>
      </div>
      {avisos.length > 0 && <div className="nota aviso" role="status"><strong>Revisa la programación:</strong><Alertas alertas={avisos} /></div>}

      <Panel estado={planner}>
        {(d) => {
          const conflictos = new Map(d.conflictos.map((c) => [c.idPt, c.alertas]));
          const partes = d.partes.filter((p) => grupo === '' || GRUPOS_ESTADO[grupo]!.estados.includes(p.estado));
          //  Una proyección es trabajo POR HACER: sólo se ve con «Todos» o «Por hacer».
          const proyecciones = grupo === '' || grupo === 'hacer' ? d.proyecciones : [];
          if (partes.length === 0 && proyecciones.length === 0) {
            //  UX-1C: texto propio de Mantenimiento (no el genérico de Limpieza).
            return (
              <div className="panel">
                <EmptyState titulo={vista === 'dia' ? 'No hay trabajos este día.' : 'No hay partes programados para esta semana.'}
                  descripcion={idCentro === '' && idOperario === '' && grupo === '' ? 'Tampoco hay preventivos previstos.' : 'Con estos filtros no hay nada. Prueba con «Todos».'} />
              </div>
            );
          }
          return (
            <div className={vista === 'dia' ? 'planner-mnt planner-mnt-vista-dia' : 'planner-mnt'}>
              {dias.map((dia) => {
                const pts = partes.filter((p) => p.fechaProgramada === dia).sort((a, b) => (a.horaProgramada ?? '').localeCompare(b.horaProgramada ?? ''));
                const prs = proyecciones.filter((o) => o.fecha === dia).sort((a, b) => a.hora.localeCompare(b.hora));
                const vacio = pts.length === 0 && prs.length === 0;
                return (
                  //  UX-1D: misma cabecera de día que el Planner de Limpieza — «hoy» marcado y la carga del día.
                  //  F12.3: en móvil, los días vacíos no ocupan pantalla (`dia-vacio`).
                  <section key={dia} className={`panel planner-mnt-dia${dia === hoyIso() ? ' hoy' : ''}${vacio ? ' dia-vacio' : ''}`} aria-label={programacion(dia, null)}
                    aria-current={dia === hoyIso() ? 'date' : undefined}>
                    <div className="panel-cabecera">
                      <h2>{vista === 'semana'
                        ? <button type="button" className="enlace" onClick={() => lista.fijarVarios({ vista: 'dia', fecha: dia === hoyIso() ? '' : dia })}>{programacion(dia, null)}</button>
                        : programacion(dia, null)}</h2>
                      {pts.length > 0 && <span className="planner-dia-carga">{pts.length} {pts.length === 1 ? 'parte' : 'partes'}</span>}
                    </div>
                    <div className="panel-cuerpo">
                      {vacio && <p className="suave pequena">Sin trabajo.</p>}
                      <ul className="planner-mnt-lista">
                        {pts.map((p) => (
                          <TarjetaPlanner key={p.id} parte={p} alertas={conflictos.get(p.id) ?? []} volver={volver}
                            mover={puede('partes:gestionar') && MOVIBLES.includes(p.estado) ? () => setMoviendo(p) : undefined} />
                        ))}
                        {prs.map((o) => (
                          <li key={`${o.plan.id}|${o.fechaOcurrencia}`} className="planner-mnt-proyeccion" aria-label={`Previsto ${o.plan.nombre}`}>
                            <div className="fila separa">
                              <strong>{o.hora}</strong>
                              <span className="etiqueta neutra">Previsto · aún sin parte</span>
                            </div>
                            <div><Link to={`/mantenimiento/preventivos/${o.plan.id}`} state={volver}>{o.plan.nombre}</Link></div>
                            <div className="suave pequena">{o.centro.nombre} · {o.tecnicos.map((t) => t.nombre).join(', ') || 'Sin técnico'}</div>
                            <Alertas alertas={o.alertas.filter((a) => a.severidad !== 'INFO')} />
                          </li>
                        ))}
                      </ul>
                    </div>
                  </section>
                );
              })}
              {vista === 'semana' && <p className="suave pequena planner-mnt-nota-movil">En el móvil sólo se muestran los días con trabajo.</p>}
            </div>
          );
        }}
      </Panel>

      {moviendo !== null && (
        <DialogoMover
          parte={moviendo}
          cerrar={() => setMoviendo(null)}
          hecho={(av) => { setMoviendo(null); setAvisos(av); planner.recargar(); }}
        />
      )}
    </Shell>
  );
}

/** Un parte en el Planner: horario, qué, dónde (centro · ubicación · activo), quién, estado, prioridad y conflicto. */
function TarjetaPlanner({ parte: p, alertas, volver, mover }: {
  parte: ParteResumen; alertas: readonly AlertaMnt[]; volver: { volver: Volver }; mover: (() => void) | undefined;
}): ReactNode {
  const donde = [p.centro.nombre, p.ubicacion, p.activos.map((a) => a.nombre).join(', ')].filter((x) => x != null && x !== '').join(' · ');
  return (
    <li className={`planner-mnt-pt tono-${estadoPtMantenimiento(p.estado).tone}`}
      aria-label={`Parte ${p.codigo} · ${estadoPtMantenimiento(p.estado).label}${alertas.length > 0 ? ' · con conflicto' : ''}`}>
      <div className="planner-mnt-cabeza">
        <strong className="planner-mnt-horario">{horario(p)}</strong>
        <EstadoPtEtiqueta estado={p.estado} size="sm" />
      </div>
      <div><MarcaDominio dominio="MANTENIMIENTO" /><Link to={`/mantenimiento/partes/${p.id}`} state={volver}>{p.titulo}</Link></div>
      <div className="suave pequena">{donde}</div>
      <div className="fila" style={{ flexWrap: 'wrap', gap: 6 }}>
        <span className="pequena">{p.tecnicos.map((t) => t.nombre).join(', ') || <span className="etiqueta aviso">Sin técnico</span>}</span>
        <PrioridadEtiqueta prioridad={p.prioridad} />
        {p.planPreventivo !== null && <span className="etiqueta neutra">Preventivo</span>}
      </div>
      {alertas.length > 0 && (
        <div className="planner-mnt-conflicto" role="note">
          <strong><span aria-hidden="true">⚠ </span>Conflicto</strong>
          <Alertas alertas={alertas} />
        </div>
      )}
      <span className="suave pequena mono">{p.codigo}</span>
      {mover !== undefined && <button className="pequeno" onClick={mover}>Mover</button>}
    </li>
  );
}

/** Mover un PT = reprogramarlo. Su ocurrencia de origen (si viene de un plan) no cambia. */
function DialogoMover({ parte: p, cerrar, hecho }: { parte: ParteResumen; cerrar: () => void; hecho: (avisos: AlertaMnt[]) => void }): ReactNode {
  const [fechaP, setFechaP] = useState(p.fechaProgramada ?? '');
  const [horaP, setHoraP] = useState(p.horaProgramada ?? '');
  //  F12.3: un parte planificado necesita su duración; si no la tiene (abierto), se puede dar aquí.
  const [duracion, setDuracion] = useState(p.duracionEstimadaMin === null ? '' : String(p.duracionEstimadaMin));
  const [error, setError] = useState<unknown>(null);
  async function enviar(ev: FormEvent): Promise<void> {
    ev.preventDefault();
    setError(null);
    try {
      const r = await api.patch<{ avisos?: AlertaMnt[] }>(`/api/mantenimiento/partes/${p.id}`, {
        fechaProgramada: fechaP, horaProgramada: horaP,
        ...(duracion !== '' && duracion !== String(p.duracionEstimadaMin ?? '') ? { duracionEstimadaMin: Number(duracion) } : {}),
      });
      hecho(r.avisos ?? []);
    } catch (e) {
      setError(e);
    }
  }
  return (
    <Modal titulo={`Mover ${p.codigo}`} cerrar={cerrar}>
      <form onSubmit={(ev) => void enviar(ev)} aria-label="Mover parte">
        <p>Reprograma este parte.{p.planPreventivo !== null && ` El plan ${p.planPreventivo.codigo} no cambia.`}</p>
        <div className="fila" style={{ flexWrap: 'wrap' }}>
          <div className="campo"><label htmlFor="mv-fecha">Fecha</label><input id="mv-fecha" type="date" required value={fechaP} onChange={(ev) => setFechaP(ev.target.value)} /></div>
          <div className="campo"><label htmlFor="mv-hora">Hora</label><input id="mv-hora" type="time" required value={horaP} onChange={(ev) => setHoraP(ev.target.value)} /></div>
          <div className="campo"><label htmlFor="mv-duracion">Duración (min)</label>
            <input id="mv-duracion" type="number" min={1} max={10080} required={p.estado === 'PLANIFICADO'} value={duracion} onChange={(ev) => setDuracion(ev.target.value)} /></div>
        </div>
        {error !== null && <ErrorCaja error={error} />}
        <div className="fila"><button type="submit" className="principal">Mover</button><button type="button" onClick={cerrar}>Cancelar</button></div>
      </form>
    </Modal>
  );
}
